import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatError } from "@/api/client";
import { toast } from "sonner";
import { Plus, X, AlertTriangle, Sparkles, RefreshCw, Search } from "lucide-react";

export default function SettingsTab({ space, onReload }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: space.name,
    slug: space.slug,
    description: space.description || "",
    settings: { ...space.settings },
    seo: { ...space.seo },
  });
  const [saving, setSaving] = useState(false);
  const [aiStatus, setAiStatus] = useState(null);
  const [searchStatus, setSearchStatus] = useState(null);
  const [reindexing, setReindexing] = useState(false);
  const [searchReindexing, setSearchReindexing] = useState(false);

  const loadAiStatus = async () => {
    try {
      const { data } = await api.get(`/spaces/${space.id}/ai/status`);
      setAiStatus(data);
    } catch { /* ignore */ }
  };
  const loadSearchStatus = async () => {
    try {
      const { data } = await api.get(`/spaces/${space.id}/search/status`);
      setSearchStatus(data);
    } catch { /* ignore */ }
  };
  useEffect(() => {
    loadAiStatus();
    loadSearchStatus();
    /* eslint-disable-next-line */
  }, [space.id]);

  const reindexAi = async () => {
    setReindexing(true);
    try {
      const { data } = await api.post(`/spaces/${space.id}/ai/reindex`);
      toast.success(`AI index rebuilt: ${data.articles} articles → ${data.chunks} chunks`);
      await loadAiStatus();
    } catch (e) { toast.error(formatError(e)); }
    finally { setReindexing(false); }
  };

  const reindexSearch = async () => {
    setSearchReindexing(true);
    try {
      const { data } = await api.post(`/spaces/${space.id}/search/reindex`);
      toast.success(`Search index rebuilt: ${data.articles} articles → ${data.records} records`);
      await loadSearchStatus();
    } catch (e) { toast.error(formatError(e)); }
    finally { setSearchReindexing(false); }
  };

  const setSetting = (k, v) => setForm({ ...form, settings: { ...form.settings, [k]: v } });
  const setSeo = (k, v) => setForm({ ...form, seo: { ...form.seo, [k]: v } });

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/spaces/${space.id}`, form);
      toast.success("Saved");
      onReload();
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  };

  const deleteSpace = async () => {
    if (!confirm("Delete this space and ALL content? This cannot be undone.")) return;
    try {
      await api.delete(`/spaces/${space.id}`);
      toast.success("Space deleted");
      navigate("/spaces");
    } catch (e) { toast.error(formatError(e)); }
  };

  const addHeaderLink = () => {
    setSetting("header_links", [...(form.settings.header_links || []), { label: "", url: "" }]);
  };
  const removeHeaderLink = (i) => {
    const arr = [...(form.settings.header_links || [])];
    arr.splice(i, 1);
    setSetting("header_links", arr);
  };
  const updateHeaderLink = (i, key, value) => {
    const arr = [...(form.settings.header_links || [])];
    arr[i] = { ...arr[i], [key]: value };
    setSetting("header_links", arr);
  };

  return (
    <div className="p-6 max-w-3xl space-y-6">
      <h2 className="text-2xl font-black tracking-tight">Settings</h2>

      <section className="bg-white border border-zinc-200 rounded-sm p-6 space-y-4">
        <h3 className="font-bold">General</h3>
        <Field label="Space name">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="setting-name"
            className="w-full px-3 py-2.5 border border-zinc-300 rounded-sm text-sm" />
        </Field>
        <Field label="Slug">
          <input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })}
            className="w-full px-3 py-2.5 border border-zinc-300 rounded-sm text-sm font-mono" />
        </Field>
        <Field label="Description">
          <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2}
            className="w-full px-3 py-2.5 border border-zinc-300 rounded-sm text-sm" />
        </Field>
      </section>

      <section className="bg-white border border-zinc-200 rounded-sm p-6 space-y-4">
        <h3 className="font-bold">Features</h3>
        <Toggle label="Show search bar" checked={form.settings.show_search} onChange={(v) => setSetting("show_search", v)} testid="toggle-search" />
        <Toggle label="Enable article ratings" checked={form.settings.show_ratings} onChange={(v) => setSetting("show_ratings", v)} testid="toggle-ratings" />
        <Toggle label="Show table of contents" checked={form.settings.show_table_of_contents} onChange={(v) => setSetting("show_table_of_contents", v)} testid="toggle-toc" />
      </section>

      <section className="bg-white border border-zinc-200 rounded-sm p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-bold">Header navigation links</h3>
          <button onClick={addHeaderLink} className="text-sm text-[#41B5E6] flex items-center gap-1 font-semibold">
            <Plus className="w-4 h-4" /> Add link
          </button>
        </div>
        {(form.settings.header_links || []).map((l, i) => (
          <div key={i} className="flex gap-2">
            <input value={l.label} placeholder="Label" onChange={(e) => updateHeaderLink(i, "label", e.target.value)}
              className="flex-1 px-3 py-2 border border-zinc-300 rounded-sm text-sm" />
            <input value={l.url} placeholder="https://…" onChange={(e) => updateHeaderLink(i, "url", e.target.value)}
              className="flex-[2] px-3 py-2 border border-zinc-300 rounded-sm text-sm font-mono" />
            <button onClick={() => removeHeaderLink(i)} className="text-zinc-500 hover:text-rose-600 px-2"><X className="w-4 h-4" /></button>
          </div>
        ))}
      </section>

      <section className="bg-white border border-zinc-200 rounded-sm p-6 space-y-4">
        <h3 className="font-bold">SEO</h3>
        <Field label="Meta title">
          <input value={form.seo.meta_title || ""} onChange={(e) => setSeo("meta_title", e.target.value)}
            className="w-full px-3 py-2.5 border border-zinc-300 rounded-sm text-sm" />
        </Field>
        <Field label="Meta description">
          <textarea value={form.seo.meta_description || ""} onChange={(e) => setSeo("meta_description", e.target.value)} rows={2}
            className="w-full px-3 py-2.5 border border-zinc-300 rounded-sm text-sm" />
        </Field>
        <Field label="Google Analytics ID">
          <input value={form.settings.google_analytics_id || ""} onChange={(e) => setSetting("google_analytics_id", e.target.value)}
            placeholder="G-XXXXXXXXXX"
            className="w-full px-3 py-2.5 border border-zinc-300 rounded-sm text-sm font-mono" />
        </Field>
        <Field label="Contact email">
          <input value={form.settings.contact_email || ""} onChange={(e) => setSetting("contact_email", e.target.value)}
            type="email"
            className="w-full px-3 py-2.5 border border-zinc-300 rounded-sm text-sm" />
        </Field>
      </section>

      <button onClick={save} disabled={saving} data-testid="settings-save"
        className="bg-[#41B5E6] hover:bg-[#2A9BCC] text-white font-semibold px-5 py-2.5 rounded-sm disabled:opacity-50">
        {saving ? "Saving…" : "Save changes"}
      </button>

      <section className="border border-rose-200 bg-rose-50/50 rounded-sm p-6">
        <div className="flex items-center gap-2 mb-3 text-rose-700 font-bold">
          <AlertTriangle className="w-4 h-4" /> Danger zone
        </div>
        <button onClick={deleteSpace} data-testid="delete-space-btn"
          className="bg-rose-600 hover:bg-rose-700 text-white font-semibold px-4 py-2 rounded-sm text-sm">
          Delete this space
        </button>
      </section>

      <section className="border border-zinc-200 bg-white rounded-sm p-6" data-testid="search-index-card">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <div className="flex items-center gap-2 font-bold">
              <Search className="w-4 h-4 text-[#41B5E6]" /> Full-text search (Algolia)
            </div>
            <p className="text-xs text-zinc-500 mt-1 max-w-md">
              Powers the public help center search and admin global search (⌘K). Articles sync automatically on publish/update. Run a full rebuild once after setup or bulk imports.
            </p>
            {searchStatus && !searchStatus.configured && (
              <p className="text-xs text-amber-700 mt-2 font-medium">
                Algolia is not configured on the server — add ALGOLIA_* env vars and restart the API.
              </p>
            )}
          </div>
          <button onClick={reindexSearch} disabled={searchReindexing || !searchStatus?.configured}
            data-testid="reindex-search-btn"
            className="flex items-center gap-2 bg-[#41B5E6] hover:bg-[#2A9BCC] disabled:opacity-50 text-white font-semibold px-4 py-2 rounded-sm text-sm">
            <RefreshCw className={`w-4 h-4 ${searchReindexing ? "animate-spin" : ""}`} />
            {searchReindexing ? "Rebuilding…" : "Rebuild search index"}
          </button>
        </div>
        {searchStatus && (
          <div className="grid grid-cols-3 gap-3 mt-4 text-center">
            <Stat label="Published" value={searchStatus.published_articles} />
            <Stat label="Indexed records" value={searchStatus.record_count} />
            <Stat label="Last rebuild" value={searchStatus.last_rebuild_at ? new Date(searchStatus.last_rebuild_at).toLocaleDateString() : "Never"} />
          </div>
        )}
      </section>

      <section className="border border-zinc-200 bg-white rounded-sm p-6" data-testid="ai-search-card">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <div className="flex items-center gap-2 font-bold">
              <Sparkles className="w-4 h-4 text-[#41B5E6]" /> AI search index
            </div>
            <p className="text-xs text-zinc-500 mt-1 max-w-md">
              Powers the "Ask AI" widget on your public portal. Articles are auto-indexed when you publish them. Rebuild if you ever change content in bulk.
            </p>
          </div>
          <button onClick={reindexAi} disabled={reindexing}
            data-testid="reindex-ai-btn"
            className="flex items-center gap-2 bg-[#41B5E6] hover:bg-[#2A9BCC] disabled:opacity-50 text-white font-semibold px-4 py-2 rounded-sm text-sm">
            <RefreshCw className={`w-4 h-4 ${reindexing ? "animate-spin" : ""}`} />
            {reindexing ? "Rebuilding…" : "Rebuild index"}
          </button>
        </div>
        {aiStatus && (
          <div className="grid grid-cols-3 gap-3 mt-4 text-center">
            <Stat label="Published" value={aiStatus.published_articles} />
            <Stat label="Indexed" value={aiStatus.indexed_articles} />
            <Stat label="Chunks" value={aiStatus.total_chunks} />
          </div>
        )}
      </section>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-zinc-50 border border-zinc-200 rounded-sm py-3 px-2">
      <div className="text-2xl font-black tracking-tight">{value ?? "—"}</div>
      <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-bold mt-1">{label}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-sm font-bold mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function Toggle({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center justify-between cursor-pointer">
      <span className="text-sm">{label}</span>
      <button type="button" role="switch" aria-checked={checked} onClick={() => onChange(!checked)}
        data-testid={testid}
        className={`w-10 h-6 rounded-full relative transition-colors ${checked ? "bg-[#41B5E6]" : "bg-zinc-300"}`}>
        <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${checked ? "translate-x-[18px]" : "translate-x-0.5"}`} />
      </button>
    </label>
  );
}
